import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { cloneDeep } from 'lodash-es';

import { API_URL } from '@constants';
import {
  AgendaPoint,
  CommunityTemplate,
  Event,
  ResourceType,
  Room,
  type Voting,
  type VotingVote,
} from '@models';
import type { VoteParent } from '@shared/components/vote/types';
import { ModalService } from './modal.service';

@Service()
export class VoteService {
  modalService = inject(ModalService);
  http = inject(HttpClient);

  getVoting = (rid: number) =>
    this.http.get<Voting>(`${API_URL}/votings/${rid}`);

  getVotingVotes = (rid: number, urlVotes: string) =>
    this.http.get<VotingVote[]>(`${API_URL}/votings/${rid}/${urlVotes}`);

  resetVoting = (rid: number) =>
    this.http.post<Voting>(`${API_URL}/votings/${rid}/reset`, null);

  voteAction = (rid: number, action: string) =>
    this.http.post<Voting>(`${API_URL}/votings/${rid}/${action}`, null);

  castVote = (rid: number, voteData: string) =>
    this.http.post<Voting & { address?: string }>(
      `${API_URL}/votings/${rid}/vote`,
      voteData,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );

  createVoting = (parentRid: number, votingData: Partial<Voting>) =>
    this.http.post<Voting>(
      `${API_URL}/votings/${parentRid}/votings`,
      votingData
    );

  updateVoting = (rid: number, votingData: Partial<Voting>) =>
    this.http.put<Voting>(`${API_URL}/votings/${rid}`, votingData);

  getCommunityVotingTemplates = () =>
    this.http.get<CommunityTemplate[]>(
      `${API_URL}/community/templates?type=${ResourceType.VOTING}`
    );

  openVote = async (rid: number, parentResource?: VoteParent) => {
    const { VoteModalComponent } =
      await import('../../shared/components/vote/vote-modal.component');

    return this.modalService.open(
      VoteModalComponent,
      { rid, parentResource },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  newVote = async (parentResource: Event | Room | AgendaPoint) => {
    const { VoteFormModalComponent } =
      await import('../../shared/components/vote/vote-form-modal.component');

    return this.modalService.open(
      VoteFormModalComponent,
      { parentResource },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };

  editVote = async (resource: Voting, parentResource: VoteParent) => {
    const { VoteFormModalComponent } =
      await import('../../shared/components/vote/vote-form-modal.component');

    return this.modalService.open(
      VoteFormModalComponent,
      {
        resource: cloneDeep(resource),
        parentResource,
        isEditMode: true,
      },
      {
        modalDialogClass: 'lg-modal',
      }
    );
  };
}
